import React, { FC, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Grid, Typography, Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { getRiskScoreRuleRequest } from '../../../../../store/actions'
import { getCrmRuleDetails, riskScoreRuleDetailsLoading } from '../../../../../store/selectors'
import { TransparentButton } from '../../../../../components/Buttons/TransparentButton'
import { Loader } from '../../../../../components/Loader'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
  },
  title: {
    lineHeight: 0,
  },
  sectionWrap: {
    width: '100%',
    margin: '32px 0',
    padding: theme.spacing(3),
    color: '#c4c4c7',
    background: 'rgba(216, 216, 216, 0.05)',
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  headerRow: {
    color: '#9b9c9f',
    marginBottom: theme.spacing(1),
  },
  rowWrap: {
    alignItems: 'center',
    padding: '8px 0',
  },
  label: {
    fontWeight: 600,
  },
  value: {
    fontWeight: 400,
  },
}))

export const RuleDetails: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams<{ id: string; tab: string }>()
  const { data: selectedRule } = useSelector(getCrmRuleDetails)
  const isLoading = useSelector(riskScoreRuleDetailsLoading)

  const handleBack = () => history.goBack()

  useEffect(() => {
    if (id) {
      dispatch(getRiskScoreRuleRequest({ id: Number(id) }))
    }
  }, [dispatch, id])

  if (isLoading || !selectedRule) {
    return <Loader />
  }

  return (
    <Box>
      <Grid container className={classes.topWrap}>
        <Typography variant="h1" className={classes.title}>
          Rule Details
        </Typography>
        <TransparentButton width={120} onClick={handleBack} text="Back" />
      </Grid>

      {/* Rule Name */}
      <Grid item container className={classes.sectionWrap}>
        <Typography className={classes.label}>
          Rule Name: <span className={classes.value}>{selectedRule.name}</span>
        </Typography>
      </Grid>

      {/* Status */}
      <Grid item container className={classes.sectionWrap}>
        <Typography className={classes.label}>
          Status: <span className={classes.value}>{selectedRule.isActive ? 'Active' : 'Inactive'}</span>
        </Typography>
      </Grid>

      {/* When section */}
      <Grid item container className={classes.sectionWrap}>
        <Typography variant="h5" className={classes.sectionHeader}>
          When
        </Typography>
        <Grid container className={classes.headerRow} spacing={2}>
          <Grid item xs={5}>
            <Typography variant="body2">Score Type</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">Operator</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">Score Level</Typography>
          </Grid>
        </Grid>
        {selectedRule.when.map((condition, index) => (
          <Grid key={index} container className={classes.rowWrap} spacing={2}>
            <Grid item xs={5}>
              <Typography>{condition.type}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography>{condition.operator}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>{condition.level}</Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>

      {/* Then section */}
      <Grid item container className={classes.sectionWrap}>
        <Typography variant="h5" className={classes.sectionHeader}>
          Then
        </Typography>
        <Grid container className={classes.headerRow} spacing={2}>
          <Grid item xs={5}>
            <Typography variant="body2">Action Type</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">Operator</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">Status</Typography>
          </Grid>
        </Grid>
        {selectedRule.then.map((action, index) => (
          <Grid key={index} container className={classes.rowWrap} spacing={2}>
            <Grid item xs={5}>
              <Typography>{action.type}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography>{action.operator}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>{action.level}</Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}
